import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, X, Trash2, Loader2, ImagePlus, Star, ChevronLeft, ChevronRight } from "lucide-react";

const MAX_PHOTOS = 6;

export default function CollectibleForm({ open, onClose, editing, onSaved, categories = [] }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("none");
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitle(editing?.title || "");
      setDescription(editing?.description || "");
      setCategoryId(editing?.category_id || "none");
      setExistingPhotos(editing?.photos || []);
      setNewFiles([]);
    }
  }, [open, editing]);

  const totalPhotos = existingPhotos.length + newFiles.length;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const available = MAX_PHOTOS - totalPhotos;
    if (files.length > available) {
      toast.error(`Maximum ${MAX_PHOTOS} photos. Only ${available} slot(s) left.`);
    }
    setNewFiles((prev) => [...prev, ...files.slice(0, available)]);
    e.target.value = "";
  };

  const removeNewFile = (idx) => setNewFiles((prev) => prev.filter((_, i) => i !== idx));

  const removeExistingPhoto = async (photoId) => {
    try {
      const res = await api.delete(`/collectibles/${editing.id}/photos/${photoId}`);
      setExistingPhotos(res.data.photos);
      toast.success("Photo removed");
    } catch {
      toast.error("Failed to remove photo");
    }
  };

  const move = (arr, from, to) => {
    const a = [...arr];
    const [item] = a.splice(from, 1);
    a.splice(to, 0, item);
    return a;
  };

  const reorderExisting = async (arr, successMsg) => {
    const prev = existingPhotos;
    setExistingPhotos(arr);
    if (!editing) {
      if (successMsg) toast.success(successMsg);
      return;
    }
    try {
      await api.put(`/collectibles/${editing.id}/photos/reorder`, { photo_ids: arr.map((p) => p.id) });
      if (successMsg) toast.success(successMsg);
    } catch {
      setExistingPhotos(prev);
      toast.error("Failed to reorder photos");
    }
  };

  const moveExisting = (i, to) => {
    if (to < 0 || to >= existingPhotos.length) return;
    reorderExisting(move(existingPhotos, i, to));
  };
  const setCoverExisting = (i) => {
    if (i === 0) return;
    reorderExisting(move(existingPhotos, i, 0), "Cover updated");
  };

  const moveNew = (i, to) => {
    if (to < 0 || to >= newFiles.length) return;
    setNewFiles(move(newFiles, i, to));
  };
  const setCoverNew = (i) => {
    if (i === 0) return;
    setNewFiles(move(newFiles, i, 0));
    toast.success("Cover updated");
  };

  // Cover = first existing photo if any exist, otherwise first newly-added file.
  const coverIsExisting = existingPhotos.length > 0;

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setSaving(true);
    try {
      const catValue = categoryId === "none" ? null : categoryId;
      if (editing) {
        await api.put(`/collectibles/${editing.id}`, { title, description, category_id: catValue });
        if (newFiles.length > 0) {
          const fd = new FormData();
          newFiles.forEach((f) => fd.append("files", f));
          await api.post(`/collectibles/${editing.id}/photos`, fd);
        }
        toast.success("Collectible updated");
      } else {
        const fd = new FormData();
        fd.append("title", title);
        fd.append("description", description);
        if (catValue) fd.append("category_id", catValue);
        newFiles.forEach((f) => fd.append("files", f));
        await api.post("/collectibles", fd);
        toast.success("Collectible created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-surface border border-white/15 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="collectible-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-700 text-2xl tracking-tight text-white uppercase">
            {editing ? "Edit Collectible" : "New Collectible"}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-white/40">
            Set a title, description and up to 6 photos for this collectible.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          <div>
            <Label className="font-mono text-xs uppercase tracking-wider text-glow-primary">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter collectible title"
              className="mt-2 bg-transparent border-white/20 text-white rounded-none focus:border-glow-primary focus-visible:ring-1 focus-visible:ring-glow-primary font-mono"
              data-testid="form-title-input"
            />
          </div>

          <div>
            <Label className="font-mono text-xs uppercase tracking-wider text-glow-primary">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this collectible..."
              rows={5}
              className="mt-2 bg-transparent border-white/20 text-white rounded-none focus:border-glow-primary focus-visible:ring-1 focus-visible:ring-glow-primary font-mono resize-none"
              data-testid="form-description-input"
            />
          </div>

          <div>
            <Label className="font-mono text-xs uppercase tracking-wider text-glow-primary">Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger
                className="mt-2 bg-transparent border-white/20 text-white rounded-none focus:border-glow-primary focus:ring-1 focus:ring-glow-primary font-mono"
                data-testid="form-category-select"
              >
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent className="bg-surface border border-white/15 rounded-none text-white">
                <SelectItem value="none" className="font-mono" data-testid="category-option-none">
                  Uncategorized
                </SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id} className="font-mono" data-testid={`category-option-${cat.id}`}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="font-mono text-xs uppercase tracking-wider text-glow-primary">
                Photos ({totalPhotos}/{MAX_PHOTOS})
              </Label>
            </div>
            <p className="font-mono text-[10px] text-white/40 mb-3">
              First photo is the gallery cover. Use the arrows to reorder or the star to set the cover.
            </p>

            <div className="grid grid-cols-3 gap-3">
              {existingPhotos.map((p, i) => {
                const isCover = coverIsExisting && i === 0;
                return (
                  <div
                    key={p.id}
                    className={`relative aspect-square border group ${isCover ? "border-glow-accent" : "border-white/10"}`}
                    data-testid={`existing-photo-${p.id}`}
                  >
                    <img src={fileUrl(p.storage_path)} alt="" className="w-full h-full object-cover" />
                    {isCover && (
                      <span
                        className="absolute top-1 left-1 bg-glow-accent text-black font-mono text-[9px] uppercase font-bold px-1.5 py-0.5 flex items-center gap-1"
                        data-testid={`cover-badge-${p.id}`}
                      >
                        <Star className="w-2.5 h-2.5 fill-black" /> Cover
                      </span>
                    )}
                    <button
                      onClick={() => removeExistingPhoto(p.id)}
                      aria-label="Remove photo"
                      className="absolute top-1 right-1 bg-glow-secondary text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-existing-photo-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="absolute bottom-0 inset-x-0 flex justify-between bg-black/70 backdrop-blur px-1 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => moveExisting(i, i - 1)}
                        disabled={i === 0}
                        aria-label="Move left"
                        className="text-white hover:text-glow-primary disabled:opacity-25"
                        data-testid={`move-left-existing-${p.id}`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCoverExisting(i)}
                        disabled={isCover}
                        aria-label="Set as cover"
                        className="text-glow-accent hover:scale-110 transition-transform disabled:opacity-25"
                        data-testid={`set-cover-existing-${p.id}`}
                      >
                        <Star className={`w-4 h-4 ${isCover ? "fill-glow-accent" : ""}`} />
                      </button>
                      <button
                        onClick={() => moveExisting(i, i + 1)}
                        disabled={i === existingPhotos.length - 1}
                        aria-label="Move right"
                        className="text-white hover:text-glow-primary disabled:opacity-25"
                        data-testid={`move-right-existing-${p.id}`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {newFiles.map((f, i) => {
                const isCover = !coverIsExisting && i === 0;
                return (
                  <div
                    key={i}
                    className={`relative aspect-square border group ${isCover ? "border-glow-accent" : "border-glow-primary/40"}`}
                    data-testid={`new-photo-${i}`}
                  >
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    {isCover && (
                      <span className="absolute top-1 left-1 bg-glow-accent text-black font-mono text-[9px] uppercase font-bold px-1.5 py-0.5 flex items-center gap-1">
                        <Star className="w-2.5 h-2.5 fill-black" /> Cover
                      </span>
                    )}
                    <button
                      onClick={() => removeNewFile(i)}
                      aria-label="Remove selected photo"
                      className="absolute top-1 right-1 bg-glow-secondary text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-new-photo-${i}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="absolute bottom-0 inset-x-0 flex justify-between bg-black/70 backdrop-blur px-1 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => moveNew(i, i - 1)}
                        disabled={i === 0}
                        aria-label="Move left"
                        className="text-white hover:text-glow-primary disabled:opacity-25"
                        data-testid={`move-left-new-${i}`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      {!coverIsExisting && (
                        <button
                          onClick={() => setCoverNew(i)}
                          disabled={isCover}
                          aria-label="Set as cover"
                          className="text-glow-accent hover:scale-110 transition-transform disabled:opacity-25"
                          data-testid={`set-cover-new-${i}`}
                        >
                          <Star className={`w-4 h-4 ${isCover ? "fill-glow-accent" : ""}`} />
                        </button>
                      )}
                      <button
                        onClick={() => moveNew(i, i + 1)}
                        disabled={i === newFiles.length - 1}
                        aria-label="Move right"
                        className="text-white hover:text-glow-primary disabled:opacity-25"
                        data-testid={`move-right-new-${i}`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {totalPhotos < MAX_PHOTOS && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square border border-dashed border-white/20 flex flex-col items-center justify-center gap-2 text-white/40 hover:border-glow-primary hover:text-glow-primary transition-colors"
                  data-testid="add-photo-btn"
                >
                  <ImagePlus className="w-6 h-6" />
                  <span className="font-mono text-[10px] uppercase">Add</span>
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 bg-glow-primary text-black font-bold hover:bg-glow-primary/80 rounded-none font-mono uppercase tracking-wider h-11 shadow-[0_0_20px_rgba(0,240,255,0.3)]"
              data-testid="form-submit-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" /> {editing ? "Save Changes" : "Create"}
                </>
              )}
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="border-white/20 text-white/70 hover:bg-white/5 rounded-none font-mono uppercase tracking-wider h-11"
              data-testid="form-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
